import React, { useState } from 'react';
import { Typography } from '../../../interfaces/typography';
import { Platform, PLATFORM_TO_HUMAN_READABLE } from '../../../interfaces/db';
import { SessionDataResult } from '../../../interfaces/services';
import { X, Share2, Copy, Phone, ExternalLink, FileText, History } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSessionList } from '../../../lib/redux/sessions/list-hook';
import { StaticFilterKeys, convertFilterToReduxSafe, getDefaultStaticFilter, updateSafeFilter } from '../../../lib/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { SessionDetailView, viewCanSeeComments, viewCanSeePastCalls, viewHasMinimalHeader } from '../types';
import { SessionStatus } from '../../SessionStatus/SessionStatus';
import { RootState } from '../../../store';
import { convertFromReduxSafeUserState, convertFromReduxSafeVisibleAccounts } from '../../../lib/redux/store';
import { useSessionDetails } from '../../../lib/redux/sessions/data-hook';

interface TranscriptHeaderProps {
  data: SessionDataResult;
  onClose?: () => void;
  onCopyTranscript?: () => void;
  view: SessionDetailView;
}

export const PreviousCalls: React.FC<{phone_number: string}> = ({phone_number}) => {
  const dispatch = useDispatch();
  const { items, isLoading, hasMore, error, loadMore, refresh } = useSessionList({
    forcedFilter: {
      ...getDefaultStaticFilter(),
      [StaticFilterKeys.PROSPECT_PHONE_OR_NAME]: [phone_number]
    }
  });
  
  const totalCallsCount = items.length;
  const previousCallsCount = totalCallsCount > 0 ? totalCallsCount - 1 : 0;
  const isLoadingPreviousCalls = isLoading;

  // Function to determine previous calls button text
  const getPreviousCallsText = () => {
    if (isLoadingPreviousCalls) return "Loading previous calls...";
    if (previousCallsCount <= 0) return "No previous calls";
    return `See ${totalCallsCount === 50 ? '50+' : previousCallsCount} previous ${previousCallsCount === 1 ? 'call' : 'calls'}`;
  }
  
  return  (
  <button
      onClick={previousCallsCount > 0 ? () => {
        dispatch(updateSafeFilter(convertFilterToReduxSafe({
        ...getDefaultStaticFilter(),
        [StaticFilterKeys.PROSPECT_PHONE_OR_NAME]: [phone_number]
      })));
    } : undefined}
  disabled={isLoadingPreviousCalls || previousCallsCount <= 0}
  className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${
    previousCallsCount > 0 
        ? 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100 cursor-pointer' 
        : 'bg-gray-50 text-gray-500 border-gray-100 cursor-default'
    } transition-colors`}
  >
    <History size={12} />
    <Typography variant="smallParagraph">
      {getPreviousCallsText()}
    </Typography>
  </button>)
}

export const TranscriptHeader: React.FC<TranscriptHeaderProps> = ({
  data,
  onClose,
  onCopyTranscript,
  view
}) => {
  const dispatch = useDispatch();
  const [showCopied, setShowCopied] = useState(false);
  const [showPhoneCopied, setShowPhoneCopied] = useState(false);
  const [showTranscriptCopied, setShowTranscriptCopied] = useState(false);

  // Get the current user and visible accounts from Redux
  const user = useSelector((state: RootState) => convertFromReduxSafeUserState(state.user));
  const visibleAccounts = useSelector((state: RootState) => 
    convertFromReduxSafeVisibleAccounts(state.visibleAccounts, (u) => u.team_is_active && u.can_dial)
  );

  const formatPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber.charAt(0) === '1') {
      return phoneNumber.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
    }
    return phoneNumber.replace(/(\d{1,4})(\d{2,4})(\d{4})/, '+$1 $2 $3');
  };

  const constructTitle = () => {
    let title = data.prospect_info?.prospect_title;
    const platform = data.session.platform;

    if (platform === Platform.SALESLOFT) {
      title = `${data.prospect_info?.prospect_seniority ?? ''} ${data.prospect_info?.prospect_title ?? ''}`.trim();
    } else if (platform === Platform.OUTREACH) {
      title = data.prospect_info?.prospect_title || data.prospect_info?.prospect_occupation;
    }

    return title?.trim() || null;
  };

  const getCompanyName = () => {
    return data.prospect_info?.company_name || null;
  };

  const constructPlatformLink = (platform: Platform, personId: string | null): string | null => {
    if (!personId) return null;

    if (platform === Platform.SALESLOFT) {
      return `https://app.salesloft.com/app/people/${personId}`;
    } else if (platform === Platform.OUTREACH) {
      return `https://web.outreach.io/prospects/${personId}/overview`;
    } 
    return null;
  };

  const getPlatformFromLink = (link: string): Platform | null => {
    if (link.includes('salesloft')) return Platform.SALESLOFT;
    else if (link.includes('outreach')) return Platform.OUTREACH;
    else if (link.includes('hubspot')) return Platform.HUBSPOT;
    else if (link.includes('freshsales')) return Platform.FRESHWORKS;
    else if (link.includes('bloobirds')) return Platform.BLOOBIRDS;
    else if (link.includes('salesforce') || link.includes('force') || link.includes('lightning')) return Platform.SALESFORCE;
    return null;
  };

  const getPlatformUrl = (): string | null => {
    if (!data.prospect_info) return null;
    
    const platform = data.session.platform;
    const personId = data.prospect_info.platform_person_id ?? data.prospect_info.xref_person_id;
    const platformLink = data.prospect_info.platform_person_url ?? data.prospect_info.xref_person_url;
    
    if (!platform || (!personId && !platformLink)) return null;
    
    return platformLink ?? constructPlatformLink(platform, personId);
  };

  const handleCopyLink = async () => {
    try {
      const url = `https://${window.location.hostname}/transcripts?id=${data.session.session_id}`;
      await navigator.clipboard.writeText(url);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleCopyPhone = async () => {
    if (!data.session.prospect_phone_value) return;
    
    try {
      await navigator.clipboard.writeText(data.session.prospect_phone_value);
      setShowPhoneCopied(true);
      setTimeout(() => setShowPhoneCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy phone number:', err);
    }
  };

  const handleCopyTranscript = () => {
    if (onCopyTranscript) {
      onCopyTranscript();
    }
    setShowTranscriptCopied(true);
    setTimeout(() => setShowTranscriptCopied(false), 2000);
  };


  const getDateFormat = (): Intl.DateTimeFormatOptions => {
    if (viewHasMinimalHeader(view)) {
      return { month: 'short', day: 'numeric' }
    }
    return { month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }
  }

  return (
    <div className="w-full border-b border-gray-100">
      <div className="flex flex-col p-2">
        {/* Name, title, date, and action buttons */}
        <div className="flex justify-between items-start">
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 max-w-[75%]">
            <Typography variant="h1" className="font-semibold truncate">
              {data.prospect_info?.prospect_name || 'Unknown Prospect'}
            </Typography>
            
            {constructTitle() && (
              <div className="flex items-center max-w-[300px] min-w-0">
                <Typography variant="mediumParagraph" color="gray" className="truncate w-full">
                  {constructTitle()}
                </Typography>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <Typography variant="mediumParagraph" color="gray">
                {data.session.requested_time.toLocaleString('en-US', getDateFormat())}
              </Typography>
              <SessionStatus 
                type="sessionData"
                sessionId={data.session.session_id}
                sessionUserId={data.session.user_id}
                viewerUserId={data.session.user_id}
              />
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {/* Session status */}
            {user && (
              <div className="flex items-center">
                <SessionStatus 
                  isMainView={true} 
                  sessionId={data.session.session_id} 
                  sessionUserId={data.session.user_id} 
                  type={'sessionData'} 
                  viewerUserId={user.user_id} 
                  reviewIsOpen={data.reviewStatus?.isOpen}
                  reviewOpenedByUser={data.reviewStatus?.openingUserId === user.user_id}
                />
              </div>
            )}

            {/* Copy transcript button */}
            {!viewHasMinimalHeader(view) && (
              <div className="relative">
                <button 
                  onClick={handleCopyTranscript}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label="Copy transcript"
                >
                  <FileText size={16} />
                </button>
                
                {/* Copy notification */}
                {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
                <AnimatePresence>
                  {showTranscriptCopied && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-full right-0 mt-1 px-2 py-1 
                        bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                    >
                      <Typography variant="smallParagraph" color="white">
                        Transcript copied!
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            {/* Share button */}
            <div className="relative">
              <button 
                onClick={(view === SessionDetailView.MAIN) ? handleCopyLink : () => {
                  window.open(`https://${window.location.hostname}/transcripts?id=${data.session.session_id}`, '_blank');
                }}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Share transcript"
              >
                <Share2 size={16} />
              </button>
              
              {/* Copy notification */}
              {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
              <AnimatePresence>
                {showCopied && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="absolute top-full right-0 mt-1 px-2 py-1 
                      bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                  >
                    <Typography variant="smallParagraph" color="white">
                      URL copied!
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            {/* Close button */}
            {onClose && (
              <button 
                onClick={onClose}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close transcript"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Group all non-name rows together */}
        <div className="flex flex-col gap-1.5">
          {/* Contact info, company, platform link */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {/* Phone number with copy functionality */}
            {data.session.prospect_phone_value && (
              <div className="relative">
                <button 
                  onClick={handleCopyPhone}
                  className="flex items-center gap-1 hover:bg-gray-50 rounded px-1 py-0.5 transition-colors group"
                >
                  <Phone size={14} className="text-gray-500" />
                  <Typography variant="mediumParagraph">
                    {formatPhoneNumber(data.session.prospect_phone_value)}
                  </Typography>
                </button>
                
                {/* Copy notification */}
                {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
                <AnimatePresence>
                  {showPhoneCopied && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-full left-0 mt-1 px-2 py-1 
                        bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                    >
                      <Typography variant="smallParagraph" color="white">
                        Copied!
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            {/* Company name */}
            {getCompanyName() && (
              <div className="flex items-center gap-1">
                <Typography variant="mediumParagraph" color="gray">
                  Company:
                </Typography>
                <Typography variant="mediumParagraph">
                  {getCompanyName()}
                </Typography>
              </div>
            )}
            
            {/* Platform - make clickable if URL exists */}
            <div className="flex items-center gap-1">
              <Typography variant="mediumParagraph" color="gray">
                Platform:
              </Typography>
              {getPlatformUrl() ? (
                <a 
                  href={getPlatformUrl() || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <Typography variant="mediumParagraph">
                    {PLATFORM_TO_HUMAN_READABLE[data.session.platform as Platform]}
                  </Typography>
                  <ExternalLink size={14} />
                </a>
              ) : (
                <Typography variant="mediumParagraph">
                  {PLATFORM_TO_HUMAN_READABLE[data.session.platform as Platform]}
                </Typography>
              )}
            </div>

            {/* Show user who placed the call if different from viewer */}
            {user && data.session.user_id !== user.user_id && (
              <div className="flex items-center gap-1">
                <Typography variant="mediumParagraph" color="gray">
                  Rep:
                </Typography>
                <Typography variant="mediumParagraph">
                  {visibleAccounts?.users.find((u) => u.user_id === data.session.user_id)?.user_name ?? "Deactivated User"}
                </Typography>
              </div>
            )}
          </div>
          
          {/* Disposition, sentiment and previous calls on their own row - always show this row for previous calls */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Previous calls indicator - always show first */}
            {viewCanSeePastCalls(view) && data.session.prospect_phone_value !== null && (
              <PreviousCalls phone_number={data.session.prospect_phone_value} />
            )}
            
            {/* Disposition */}
            {data.sessionMetric?.user_disposition && (
              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 rounded border border-gray-100">
                <Typography variant="mediumParagraph" color="gray">
                  Disposition:
                </Typography>
                <Typography variant="mediumParagraph">
                  {data.sessionMetric.user_disposition.charAt(0).toUpperCase() + 
                   data.sessionMetric.user_disposition.slice(1).toLowerCase()}
                </Typography>
              </div>
            )}
            
            {/* Sentiment */}
            {data.sessionMetric?.user_sentiment && (
              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-50 rounded border border-gray-100">
                <Typography variant="mediumParagraph" color="gray">
                  Sentiment:
                </Typography>
                <Typography variant="mediumParagraph">
                  {data.sessionMetric.user_sentiment.charAt(0).toUpperCase() + 
                   data.sessionMetric.user_sentiment.slice(1).toLowerCase()}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 